// Full incubator (INCUBATION-BREEDING.md §2-5, GENETICS.md §5).
//
// A bred (fertile) egg can go under the lamp instead of into the pan:
// temperature, humidity, turning, candling days 7/14, lockdown day 18,
// hatch day 21. Care is scored per day; neglect stops development.
// Framing is kid-safe throughout: eggs "stop developing", never die.
//
// State lives on the egg (`StoredEgg.incubation`), so old saves load fine
// and the Eggs chore only needs to skip active ones.

import { createChick } from '../core/initialState';
import { Rng } from '../core/rng';
import type { ChickenEntity, EggIncubation, GameState, StoredEgg } from '../core/types';
import { effectsOf } from './items';
import { layoutStructures } from './structureLayout';

export const INCUBATION_DAYS = 21;
export const IDEAL_TEMP_F = 99.5;
export const TEMP_OK_F = 1.0;
export const TEMP_MIN_F = 95;
export const TEMP_MAX_F = 103;
export const IDEAL_RH = 55;
export const LOCKDOWN_RH = 65;
export const RH_OK = 7;
export const RH_MIN = 30;
export const RH_MAX = 80;
export const TURNS_PER_DAY = 3;
export const CANDLE_DAYS = [7, 14] as const;
export const LOCKDOWN_DAY = 18;

export function isFertile(egg: StoredEgg): boolean {
  return egg.fertile === true;
}

export function isIncubating(egg: StoredEgg): boolean {
  return egg.incubation !== undefined && egg.incubation.failed !== true;
}

export function incubationDay(egg: StoredEgg, simHour: number): number | null {
  if (!egg.incubation) return null;
  return Math.floor((simHour - egg.incubation.sinceHour) / 24);
}

/** Every fertile egg sitting in a nest box, oldest first. */
export function fertileEggs(state: GameState): StoredEgg[] {
  return state.structures.flatMap((s) => s.state.eggs ?? []).filter(isFertile);
}

function findEgg(state: GameState, eggId: string): StoredEgg | undefined {
  for (const s of state.structures) {
    const egg = (s.state.eggs ?? []).find((e) => e.id === eggId);
    if (egg) return egg;
  }
  return undefined;
}

function removeEgg(state: GameState, eggId: string): void {
  for (const s of state.structures) {
    if (!s.state.eggs) continue;
    s.state.eggs = s.state.eggs.filter((e) => e.id !== eggId);
  }
}

/**
 * Puts a fertile egg under the lamp. Needs a brooder (the heat source) and
 * an egg that isn't already cooking. Returns false with no side effects.
 */
export function startIncubation(state: GameState, eggId: string): boolean {
  const egg = findEgg(state, eggId);
  if (!egg || !isFertile(egg) || egg.incubation) return false;
  const hasBrooder = state.structures.some((s) => effectsOf(s).brooder);
  if (!hasBrooder) return false;
  const rec: EggIncubation = {
    sinceHour: state.simHour,
    tempF: IDEAL_TEMP_F,
    humidity: IDEAL_RH,
    turnsToday: 0,
    lastTurnDay: 0,
    lastDay: 0,
    goodDays: 0,
    candled7: false,
    candled14: false,
    lockdown: false,
  };
  egg.incubation = rec;
  return true;
}

/** One turn. Lockdown eggs and finished records refuse. */
export function turnEgg(state: GameState, eggId: string): boolean {
  const egg = findEgg(state, eggId);
  const rec = egg?.incubation;
  if (!egg || !rec || rec.failed || rec.lockdown) return false;
  const day = Math.floor((state.simHour - rec.sinceHour) / 24);
  if (day !== rec.lastTurnDay) {
    rec.lastTurnDay = day;
    rec.turnsToday = 0;
  }
  if (rec.turnsToday >= TURNS_PER_DAY + 2) return false;
  rec.turnsToday += 1;
  return true;
}

export function setIncubatorTemp(state: GameState, eggId: string, tempF: number): boolean {
  const egg = findEgg(state, eggId);
  if (!egg?.incubation || egg.incubation.failed) return false;
  egg.incubation.tempF = Math.min(TEMP_MAX_F, Math.max(TEMP_MIN_F, Math.round(tempF * 2) / 2));
  return true;
}

export function setIncubatorHumidity(state: GameState, eggId: string, rh: number): boolean {
  const egg = findEgg(state, eggId);
  if (!egg?.incubation || egg.incubation.failed) return false;
  egg.incubation.humidity = Math.min(RH_MAX, Math.max(RH_MIN, Math.round(rh)));
  return true;
}

export type CandleResult =
  | { ok: false; reason: 'no_egg' | 'not_incubating' | 'too_early' | 'done' }
  | { ok: true; viable: boolean; day: number };

/**
 * Days 7/14: hold the egg to the light. Viable = developing (not failed and
 * on track). Observing never harms — the lesson is look, don't shake.
 */
export function candleEgg(state: GameState, eggId: string): CandleResult {
  const egg = findEgg(state, eggId);
  if (!egg) return { ok: false, reason: 'no_egg' };
  const rec = egg.incubation;
  if (!rec || rec.failed) return { ok: false, reason: 'not_incubating' };
  const day = Math.floor((state.simHour - rec.sinceHour) / 24);
  if (day >= 7 && day < 10 && !rec.candled7) {
    rec.candled7 = true;
    return { ok: true, viable: true, day };
  }
  if (day >= 14 && day < 17 && !rec.candled14) {
    rec.candled14 = true;
    return { ok: true, viable: true, day };
  }
  if (day >= INCUBATION_DAYS) return { ok: false, reason: 'done' };
  return { ok: false, reason: 'too_early' };
}

function scoreDay(rec: EggIncubation): boolean {
  const tempOk = Math.abs(rec.tempF - IDEAL_TEMP_F) <= TEMP_OK_F;
  const targetRh = rec.lockdown ? LOCKDOWN_RH : IDEAL_RH;
  const rhOk = Math.abs(rec.humidity - targetRh) <= RH_OK;
  const turnsOk = rec.lockdown || rec.turnsToday >= TURNS_PER_DAY;
  return tempOk && rhOk && turnsOk;
}

/**
 * Advances every incubating egg. Day boundaries score yesterday's care;
 * gross neglect (heat >3°F off, or zero turns outside lockdown) stops
 * development. Day 21 with a clean-enough record hatches — using the stored
 * genotype, never a re-roll. Returns the new chicks.
 */
export function stepIncubation(state: GameState, rng: Rng): ChickenEntity[] {
  const hatched: ChickenEntity[] = [];
  const eggs = state.structures.flatMap((s) => s.state.eggs ?? []).filter(isIncubating);
  for (const egg of eggs) {
    const rec = egg.incubation!;
    const day = Math.floor((state.simHour - rec.sinceHour) / 24);
    if (day > rec.lastDay) {
      for (let d = rec.lastDay; d < day; d++) {
        if (d >= LOCKDOWN_DAY) rec.lockdown = true;
        if (d === 0) continue; // set day doesn't score
        // Turning resets with each new day.
        if (scoreDay({ ...rec, turnsToday: rec.turnsToday })) rec.goodDays += 1;
        const heatOff = Math.abs(rec.tempF - IDEAL_TEMP_F);
        if (heatOff > 3 || (!rec.lockdown && rec.turnsToday === 0)) {
          rec.failed = true;
          break;
        }
      }
      rec.turnsToday = 0;
      rec.lastTurnDay = day;
      rec.lastDay = day;
    }
    if (rec.failed) continue;
    if (day < INCUBATION_DAYS) continue;
    // Hatch: clean-enough means at least 2/3 of scored days in spec.
    const scoredDays = Math.max(1, INCUBATION_DAYS - 1);
    if (rec.goodDays < Math.ceil(scoredDays * (2 / 3))) {
      rec.failed = true;
      continue;
    }
    const chick = hatchBredEgg(state, egg, rng);
    removeEgg(state, egg.id);
    state.flock.push(chick);
    hatched.push(chick);
  }
  return hatched;
}

/** Builds the chick from the stored cross — the verify half of predict→verify. */
export function hatchBredEgg(state: GameState, egg: StoredEgg, rng: Rng): ChickenEntity {
  const hen = state.flock.find((c) => c.id === egg.layerId);
  const breed = hen?.breed ?? 'rhode_island_red';
  const brooder = state.structures.find((s) => effectsOf(s).brooder);
  const spot = brooder ? layoutStructures(state).get(brooder.id) : undefined;
  const chick = createChick(
    rng,
    breed,
    chickName(rng),
    { x: spot?.x ?? 0, y: spot?.y ?? 0 },
    0,
    rng.chance(0.5) ? 'hen' : 'rooster',
    Math.floor(state.simHour / 24),
  );
  if (egg.genotype) chick.genotype = { ...egg.genotype };
  if (egg.sireId) chick.parentIds = [egg.sireId, egg.layerId];
  return chick;
}

const HATCH_NAMES = ['Pío', 'Nube2', 'Cáscara', 'Tibio', 'Alba', 'Mote'];

function chickName(rng: Rng): string {
  return HATCH_NAMES[rng.int(0, HATCH_NAMES.length - 1)];
}
